import { datadogRum } from "@datadog/browser-rum";

import { logMiscError } from "@brexhq/next/src/telemetry/logging";
import { isDev } from "@brexhq/shared/utils/env/isDevEnv";

// these errors should not be tracked because they are expected, created by
// external libraries, and/or have no negative effect.
const DO_NOT_TRACK_ERRORS: (string | RegExp)[] = [
  // ResizeObserver errors have no negative effect and create a ton of noise
  // @see https://stackoverflow.com/a/50387233
  /^Error: ResizeObserver/,
  // Nextjs and react 18 errors aren't providing enough information for us in datadog, we know they're there for everysession so no point sending them considering there are so many of them
  /^Error: This Suspense/,
  /^This Suspense boundary/i,
  /^Expected server HTML/,

  // filter out error that might be caused by link validation/ pre-fetch rendering from email clicks
  // https://github.com/DataDog/browser-sdk/issues/2715
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,

  // third party script errors have no debugging information attached to them
  // for security purposes and are out of our control
  /Script error\./,
];

interface NetworkInformation {
  downlink?: number;
  effectiveType?: string;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

export const initDatadogRum = () => {
  const applicationId = process.env.DATADOG_RUM_ID;
  const clientToken = process.env.DATADOG_RUM_TOKEN;
  const releaseVersion = process.env.VERCEL_GIT_COMMIT_SHA;

  if (applicationId && clientToken && !isDev) {
    try {
      datadogRum.init({
        applicationId,
        clientToken,
        sessionReplaySampleRate: 100,
        sessionSampleRate: 100,
        trackUserInteractions: false,
        service: "website---next.js",
        version: releaseVersion,
        beforeSend(event) {
          if (
            event.type === "error" &&
            DO_NOT_TRACK_ERRORS.some((ignoreError) =>
              ignoreError instanceof RegExp
                ? ignoreError.test(event.error.message)
                : ignoreError === event.error.message,
            )
          ) {
            return false;
          }
          try {
            let contextEnrichment = {};
            // @ts-expect-error navigator.connection should exist on some browsers. See Network Information API: https://developer.mozilla.org/en-US/docs/Web/API/Network_Information_API
            if (navigator.connection) {
              const networkInformation =
                // @ts-expect-error navigator.connection should exist on some browsers. See Network Information API: https://developer.mozilla.org/en-US/docs/Web/API/Network_Information_API
                navigator.connection as NetworkInformation;
              contextEnrichment = {
                networkDownlink: networkInformation.downlink,
                networkEffectiveType: networkInformation.effectiveType,
                networkRtt: networkInformation.rtt,
                networkSaveData: networkInformation.saveData,
                networkType: networkInformation.type,
              };
            }
            if (Object.keys(contextEnrichment).length !== 0) {
              // datadog requires for event.context has to be as flat as possible.
              // this means that anything that goes into context cannot be nested more than 1 level deep
              // this is OK ✅: event.context.someParam = "someValue" is ok
              // this is Not OK ❌: event.context.nested.someParam = "someValue"
              event.context = {
                ...event.context,
                ...contextEnrichment,
              };
            }
            return true;
          } catch (err) {
            logMiscError("Failed to enrich datadog RUM event");
            return true;
          }
        },
      });
      datadogRum.startSessionReplayRecording();
    } catch {
      logMiscError("Failed to initialize datadog RUM");
    }
  }
};
